import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  Package,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Download,
  Edit,
  Trash2,
  ShoppingCart,
  DollarSign,
  Warehouse,
  X
} from 'lucide-react';
import { branchService } from '../../../services/branchService';
import { warehouseService } from '../../../services/warehouseService';
import { inventoryService } from '../../../services/inventoryService';
import { supplierService } from '../../../services/supplierService';

const Inventory = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showNewSupplierForm, setShowNewSupplierForm] = useState(false);
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', code: '', contactPerson: '', email: '', phone: '' });
  const [formData, setFormData] = useState({
    warehouseId: '',
    name: '',
    sku: '',
    category: 'SPARE_PART',
    description: '',
    quantityOnHand: '',
    minimumQuantity: '',
    reorderPoint: '',
    unitOfMeasure: 'UNITS',
    unitCost: '',
    supplierId: '',
    supplierPartNumber: '',
    leadTimeDays: '',
    binLocation: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [branchesData, warehousesData, inventoryData, suppliersData] = await Promise.all([
        branchService.getBranches(),
        warehouseService.getWarehouses(),
        inventoryService.getInventory(),
        supplierService.getSuppliers()
      ]);
      setBranches(branchesData || []);
      setWarehouses(warehousesData || []);
      setInventoryItems(inventoryData || []);
      setSuppliers(suppliersData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setBranches([]);
      setWarehouses([]);
      setInventoryItems([]);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  // Get warehouses filtered by selected branch
  const getFilteredWarehouses = () => {
    if (filterBranch === 'all') return warehouses;
    return warehouses.filter(w => w.branchId === parseInt(filterBranch));
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      warehouseId: '',
      name: '',
      sku: '',
      category: 'SPARE_PART',
      description: '',
      quantityOnHand: '',
      minimumQuantity: '',
      reorderPoint: '',
      unitOfMeasure: 'UNITS',
      unitCost: '',
      supplierId: '',
      supplierPartNumber: '',
      leadTimeDays: '',
      binLocation: ''
    });
    setShowNewSupplierForm(false);
    setNewSupplier({ name: '', code: '', contactPerson: '', email: '', phone: '' });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      warehouseId: item.warehouseId || '',
      name: item.name || '',
      sku: item.sku || '',
      category: item.category || 'SPARE_PART',
      description: item.description || '',
      quantityOnHand: item.quantityOnHand || '',
      minimumQuantity: item.minimumQuantity || '',
      reorderPoint: item.reorderPoint || '',
      unitOfMeasure: item.unitOfMeasure || 'UNITS',
      unitCost: item.unitCost || '',
      supplierId: item.supplierId || '',
      supplierPartNumber: item.supplierPartNumber || '',
      leadTimeDays: item.leadTimeDays || '',
      binLocation: item.binLocation || ''
    });
    setShowNewSupplierForm(false);
    setNewSupplier({ name: '', code: '', contactPerson: '', email: '', phone: '' });
    setShowModal(true);
  };

  const handleSupplierChange = (value) => {
    if (value === 'new') {
      setShowNewSupplierForm(true);
      setFormData(prev => ({ ...prev, supplierId: '' }));
    } else {
      setShowNewSupplierForm(false);
      setFormData(prev => ({ ...prev, supplierId: value }));
    }
  };

  const handleCreateSupplier = async () => {
    if (!newSupplier.name.trim()) {
      alert('Supplier name is required');
      return;
    }
    setSavingSupplier(true);
    try {
      const created = await supplierService.createSupplier({
        ...newSupplier,
        code: newSupplier.code || `SUP-${Date.now().toString().slice(-6)}`,
        supplierType: 'PARTS',
        status: 'ACTIVE'
      });
      setSuppliers(prev => [...prev, created]);
      setFormData(prev => ({ ...prev, supplierId: created.id }));
      setNewSupplier({ name: '', code: '', contactPerson: '', email: '', phone: '' });
      setShowNewSupplierForm(false);
    } catch (error) {
      console.error('Error creating supplier:', error);
      alert('Error creating supplier. Please try again.');
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await inventoryService.updateInventory(editingItem.id, formData);
      } else {
        await inventoryService.createInventory(formData);
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving inventory item:', error);
      alert('Failed to save inventory item. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this inventory item?')) {
      try {
        await inventoryService.deleteInventory(id);
        loadData();
      } catch (error) {
        console.error('Error deleting inventory item:', error);
        alert('Failed to delete inventory item. Please try again.');
      }
    }
  };

  // Calculate categories from inventory items
  const categories = inventoryItems.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = { name: category, items: 0, value: 0 };
    }
    acc[category].items++;
    acc[category].value += item.totalValue || 0;
    return acc;
  }, {});
  const categoryList = Object.values(categories).map(c => ({
    ...c,
    value: `$${c.value.toLocaleString()}`
  }));

  const getStatusBadge = (item) => {
    const qty = item.quantityOnHand || 0;
    const minQty = item.minimumQuantity || 0;
    if (qty === 0) {
      return { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: t('cmms.inv_outOfStock') };
    } else if (qty <= minQty || item.isLowStock) {
      return { color: 'bg-yellow-100 text-yellow-800', icon: TrendingDown, label: t('cmms.inv_lowStock') };
    } else {
      return { color: 'bg-green-100 text-green-800', icon: TrendingUp, label: t('cmms.inv_inStock') };
    }
  };

  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.sku || '').toLowerCase().includes(searchTerm.toLowerCase());
    const qty = item.quantityOnHand || 0;
    const minQty = item.minimumQuantity || 0;
    const matchesFilter = filterStatus === 'all' ||
                         (filterStatus === 'low' && qty <= minQty && qty > 0) ||
                         (filterStatus === 'out' && qty === 0) ||
                         (filterStatus === 'good' && qty > minQty);
    const matchesBranch = filterBranch === 'all' || item.branchId === parseInt(filterBranch);
    const matchesWarehouse = filterWarehouse === 'all' || item.warehouseId === parseInt(filterWarehouse);
    return matchesSearch && matchesFilter && matchesBranch && matchesWarehouse;
  });

  const totalValue = inventoryItems.reduce((sum, item) => sum + (item.totalValue || 0), 0);
  const lowStockCount = inventoryItems.filter(item => item.isLowStock || (item.quantityOnHand <= item.minimumQuantity)).length;
  const outOfStockCount = inventoryItems.filter(item => item.quantityOnHand === 0).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.inventory')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.inv_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">
            <Download className="w-5 h-5 mr-2" />
            {t('common.export')}
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('common.add')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-8 h-8 text-blue-500" />
            <div>
              <div className="text-2xl font-bold text-gray-900">{inventoryItems.length}</div>
              <div className="text-sm text-gray-600">{t('cmms.inv_totalItems')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-8 h-8 text-green-500" />
            <div>
              <div className="text-2xl font-bold text-gray-900">${totalValue.toLocaleString()}</div>
              <div className="text-sm text-gray-600">{t('cmms.inv_totalValue')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <TrendingDown className="w-8 h-8 text-yellow-500" />
            <div>
              <div className="text-2xl font-bold text-yellow-600">{lowStockCount}</div>
              <div className="text-sm text-gray-600">{t('cmms.inv_lowStock')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div>
              <div className="text-2xl font-bold text-red-600">{outOfStockCount}</div>
              <div className="text-sm text-gray-600">{t('cmms.inv_outOfStock')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <select
            value={filterBranch}
            onChange={(e) => {
              setFilterBranch(e.target.value);
              setFilterWarehouse('all'); // Reset warehouse when branch changes
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">{t('cmms.wh_allBranches')}</option>
            {branches.map(branch => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>

          <select
            value={filterWarehouse}
            onChange={(e) => setFilterWarehouse(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">{t('cmms.inv_allWarehouses')}</option>
            {getFilteredWarehouses().map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">{t('common.allStatus')}</option>
            <option value="good">{t('cmms.inv_inStock')}</option>
            <option value="low">{t('cmms.inv_lowStock')}</option>
            <option value="out">{t('cmms.inv_outOfStock')}</option>
          </select>

          <select className="px-4 py-2 border border-gray-300 rounded-lg">
            <option>{t('cmms.inv_allCategories')}</option>
            <option>Filters</option>
            <option>Bearings</option>
            <option>Belts</option>
            <option>Seals</option>
            <option>Lubricants</option>
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Inventory Table */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.inv_partNumber')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.inv_warehouse')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.inv_bin')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.inv_quantity')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.inv_value')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.map((item) => {
                  const statusBadge = getStatusBadge(item);
                  const StatusIcon = statusBadge.icon;
                  const qty = item.quantityOnHand || 0;
                  const reorderPoint = item.reorderPoint || item.minimumQuantity || 10;
                  const stockPercentage = reorderPoint > 0 ? (qty / (reorderPoint * 2)) * 100 : 100;

                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.sku}</div>
                        <div className="text-xs text-gray-500">ID: {item.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.category}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Warehouse className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-sm text-gray-900">{item.warehouseName || '-'}</div>
                            <div className="text-xs text-gray-500">{item.branchName || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {item.binLocation || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{qty} {item.unitOfMeasure || ''}</div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div
                            className={`h-1.5 rounded-full ${
                              stockPercentage > 50 ? 'bg-green-500' :
                              stockPercentage > 20 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(stockPercentage, 100)}%` }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        ${(item.totalValue || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <ShoppingCart className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Categories */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">{t('cmms.inv_categories')}</h3>
            <div className="space-y-3">
              {categoryList.length > 0 ? categoryList.map((category, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                  <div>
                    <div className="font-medium text-gray-900">{category.name}</div>
                    <div className="text-xs text-gray-500">{category.items} {t('cmms.wh_items')}</div>
                  </div>
                  <div className="text-sm font-bold text-primary-600">{category.value}</div>
                </div>
              )) : (
                <div className="text-sm text-gray-500 text-center py-4">
                  {t('common.noData')}
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">{t('cmms.inv_summary')}</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700">{t('cmms.inv_totalItems')}</span>
                </div>
                <span className="font-bold text-blue-600">{inventoryItems.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-50">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-gray-700">{t('cmms.inv_lowStock')}</span>
                </div>
                <span className="font-bold text-yellow-600">{lowStockCount}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-red-50">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-gray-700">{t('cmms.inv_outOfStock')}</span>
                </div>
                <span className="font-bold text-red-600">{outOfStockCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingItem ? t('common.edit') : t('common.add')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Warehouse Selection */}
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  <Warehouse className="w-4 h-4 inline mr-2" />
                  {t('cmms.inv_warehouse')} *
                </label>
                <select
                  value={formData.warehouseId}
                  onChange={(e) => setFormData({...formData, warehouseId: e.target.value})}
                  className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  required
                >
                  <option value="">{t('cmms.inv_selectWarehouse')}</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} ({warehouse.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_sku')} *</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="e.g., SKU-001"
                    disabled={editingItem}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="e.g., Oil Filter"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_category')} *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="SPARE_PART">{t('cmms.inv_catSpareParts')}</option>
                    <option value="CONSUMABLE">{t('cmms.inv_catConsumables')}</option>
                    <option value="TOOL">{t('cmms.inv_catTools')}</option>
                    <option value="SAFETY_EQUIPMENT">{t('cmms.inv_catSafetyEquipment')}</option>
                    <option value="MATERIAL">{t('cmms.inv_catMaterial')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_unitOfMeasure')}</label>
                  <select
                    value={formData.unitOfMeasure}
                    onChange={(e) => setFormData({...formData, unitOfMeasure: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="UNITS">{t('cmms.inv_uomUnits')}</option>
                    <option value="KG">{t('cmms.inv_uomKg')}</option>
                    <option value="LBS">{t('cmms.inv_uomLbs')}</option>
                    <option value="LITERS">{t('cmms.inv_uomLiters')}</option>
                    <option value="METERS">{t('cmms.inv_uomMeters')}</option>
                    <option value="FEET">{t('cmms.inv_uomFeet')}</option>
                    <option value="BOXES">{t('cmms.inv_uomBoxes')}</option>
                    <option value="SETS">{t('cmms.inv_uomSets')}</option>
                  </select>
                </div>
              </div>

              {/* Stock Levels */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('cmms.inv_stockLevels')}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_quantityOnHand')} *</label>
                    <input
                      type="number"
                      value={formData.quantityOnHand}
                      onChange={(e) => setFormData({...formData, quantityOnHand: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_minimumQuantity')}</label>
                    <input
                      type="number"
                      value={formData.minimumQuantity}
                      onChange={(e) => setFormData({...formData, minimumQuantity: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_reorderPoint')}</label>
                    <input
                      type="number"
                      value={formData.reorderPoint}
                      onChange={(e) => setFormData({...formData, reorderPoint: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing & Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_unitCost')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.unitCost}
                    onChange={(e) => setFormData({...formData, unitCost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_binLocation')}</label>
                  <input
                    type="text"
                    value={formData.binLocation}
                    onChange={(e) => setFormData({...formData, binLocation: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., A-1-3"
                  />
                </div>
              </div>

              {/* Supplier Info */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('cmms.inv_supplierInfo')}</h3>

                {/* Supplier selector */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_supplier')}</label>
                  <select
                    value={showNewSupplierForm ? 'new' : (formData.supplierId || '')}
                    onChange={(e) => handleSupplierChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('cmms.inv_noSupplier')}</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                    <option value="new">{t('cmms.inv_addNewSupplier')}</option>
                  </select>
                </div>

                {/* Inline new supplier form */}
                {showNewSupplierForm && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3 space-y-3">
                    <h4 className="text-sm font-semibold text-green-900">{t('cmms.inv_newSupplier')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.name')} *</label>
                        <input
                          type="text"
                          value={newSupplier.name}
                          onChange={(e) => setNewSupplier(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          placeholder="Supplier name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('cmms.sup_contactPerson')}</label>
                        <input
                          type="text"
                          value={newSupplier.contactPerson}
                          onChange={(e) => setNewSupplier(prev => ({ ...prev, contactPerson: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          placeholder="Contact name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.email')}</label>
                        <input
                          type="email"
                          value={newSupplier.email}
                          onChange={(e) => setNewSupplier(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.phone')}</label>
                        <input
                          type="text"
                          value={newSupplier.phone}
                          onChange={(e) => setNewSupplier(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          placeholder="+1 234 567 8900"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => { setShowNewSupplierForm(false); setFormData(prev => ({ ...prev, supplierId: '' })); }}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateSupplier}
                        disabled={savingSupplier || !newSupplier.name.trim()}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {savingSupplier ? t('common.loading') : t('cmms.inv_saveSupplier')}
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_supplierPartNumber')}</label>
                    <input
                      type="text"
                      value={formData.supplierPartNumber}
                      onChange={(e) => setFormData({...formData, supplierPartNumber: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., SUP-PART-001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.inv_leadTime')}</label>
                    <input
                      type="number"
                      value={formData.leadTimeDays}
                      onChange={(e) => setFormData({...formData, leadTimeDays: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="0"
                      placeholder="Days"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingItem ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
